import App from './components/App.svelte';
import { VViewer, compactOptions, version } from './lib';

type AppOptions = ConstructorParameters<typeof App>[0];

export function start(
  props: AppOptions['props'] = {},
  target: AppOptions['target'] = document.body
): App {
  return new App({ props, target });
}

export function parseOptions(queryString: string): AppOptions['props'] {
  const params = new URLSearchParams(queryString);
  const toValue = <T>(key: string, fn: (value: string) => T | undefined): T | undefined =>
    params.has(key) ? fn(params.get(key) || '') : undefined;
  const toBool = (key: string): boolean | undefined => toValue(key, (v) => v !== '0');
  const toNumber = (key: string): number | undefined =>
    toValue(key, (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    });

  return compactOptions<NonNullable<AppOptions['props']>>({
    colorScheme: toBool('dark') ? 'dark' : undefined,
    bigButtonEnabled: toBool('big_button'),
    viewerHintEnabled: toBool('viewer_hint'),
    autoStart: toBool('autostart'),
    attributionMessage: toValue('attribution', String),
    thumbnailBackgroundColor: toValue('thumbnail_bgcolor', String),
    thumbnailUrl: toValue('thumbnail', String),
    meshUrl: toValue('glb', String),
    usdzUrl: toValue('usdz', String),
    highDefinition: toBool('hd'),
    sceneClearColor: toValue('bgcolor', String),
    cameraRadius: toNumber('radius'),
    cameraAlpha: toNumber('alpha'),
    cameraBeta: toNumber('beta'),
    cameraLowerAlphaLimit: toNumber('min_alpha'),
    cameraUpperAlphaLimit: toNumber('max_alpha'),
    cameraLowerBetaLimit: toNumber('min_beta'),
    cameraUpperBetaLimit: toNumber('max_beta'),
    cameraAutoRotation: toBool('autospin'),
    groundEnabled: toBool('ground'),
    groundMirrorEnabled: toBool('mirror'),
    skyboxEnabled: toBool('skybox'),
    gridEnabled: toBool('grid'),
    controlsEnabled: toBool('controls'),
    brandEnabled: toBool('brand'),
    inspectorEnabled: toBool('inspector'),
    inspectorOpened: toBool('open_inspector'),
    versionVisibility: toBool('version'),
    channelsEnabled: toBool('channels'),
    filesInputEnabled: toBool('files'),
    arEnabled: toBool('ar'),
    debugLayer: toBool('open_debug'),
    errorMessagesVisibility: toBool('error'),
  });
}

export function options(
  defaults: AppOptions['props'] = {},
  getQueryString = (): string =>
    /^#\?/.test(window.location.hash) ? window.location.hash.slice(1) : window.location.search
): AppOptions['props'] {
  return { ...defaults, ...parseOptions(getQueryString()) };
}

export function create(
  ...args: Parameters<typeof VViewer.create>
): ReturnType<typeof VViewer.create> {
  return VViewer.create(...args);
}

export const VERSION = version;
